import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CategoryManager from "../components/CategoryManager";
import ExportToExcelButton from "../components/ExportToExcelButton";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Plus,
  Search,
  Filter,
  FileText,
  Loader2,
  ArrowRight,
  Trash2,
  Download,
  CheckSquare,
  Square,
  Upload,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency, formatDate, getStatusClass, QUOTE_STATUSES } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuotesList = () => {
  const { isOfficeManager, isQuoter } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteQuoteId, setDeleteQuoteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [companyFilter, setCompanyFilter] = useState(searchParams.get("company") || "all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkExporting, setBulkExporting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === quotes.length) setSelectedIds([]);
    else setSelectedIds(quotes.map((q) => q.id));
  };
  const handleBulkExport = async (format) => {
    if (selectedIds.length === 0) return;
    setBulkExporting(true);
    try {
      const response = await axios.post(
        `${API}/quotes/bulk-export`,
        { quote_ids: selectedIds, format },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `quotes-export-${stamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedIds.length} quotes as ${format.toUpperCase()}`);
      setSelectedIds([]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to bulk export");
    } finally {
      setBulkExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, companyFilter]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (companyFilter && companyFilter !== "all") params.append("company_id", companyFilter);
      if (searchQuery) params.append("search", searchQuery);

      const [quotesRes, companiesRes] = await Promise.all([
        axios.get(`${API}/quotes?${params.toString()}`),
        axios.get(`${API}/companies`),
      ]);
      setQuotes(quotesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Failed to fetch quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleDeleteQuote = async () => {
    if (!deleteQuoteId) return;
    try {
      await axios.delete(`${API}/quotes/${deleteQuoteId}`);
      toast.success("Quote deleted");
      setDeleteQuoteId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  const updateFilters = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);

    if (key === "status") setStatusFilter(value || "all");
    if (key === "company") setCompanyFilter(value || "all");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            Quotes
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage all your quotes and work orders.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(isOfficeManager || isQuoter) && (
            <Link to="/quotes/upload" data-testid="upload-quote-link">
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Upload Quote
              </Button>
            </Link>
          )}
          {(isOfficeManager || isQuoter) && (
            <Link to="/quotes/new-from-template" data-testid="new-from-template-link">
              <Button variant="outline">
                <Sparkles className="h-4 w-4 mr-2" />
                From Template
              </Button>
            </Link>
          )}
          {(isOfficeManager || isQuoter) && (
            <Link to="/bulk-upload?kind=quote">
              <Button variant="outline" data-testid="bulk-upload-quote-btn">
                <Sparkles className="h-4 w-4 mr-2" />
                Mass Upload
              </Button>
            </Link>
          )}
          {(isOfficeManager || isQuoter) && (
            <ExportToExcelButton path="quotes" label="Excel" />
          )}
          {(isOfficeManager || isQuoter) && (
            <Link to="/quotes/new">
              <Button data-testid="new-quote-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Quote
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Bulk action bar — only shown when selections exist */}
      {selectedIds.length > 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="bulk-action-bar">
          <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3">
            <span className="font-medium text-sm">
              {selectedIds.length} selected
            </span>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedIds([])}
              data-testid="clear-selection-btn"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => handleBulkExport("pdf")}
              disabled={bulkExporting}
              data-testid="bulk-export-pdf-btn"
            >
              {bulkExporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export PDFs (ZIP)
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleBulkExport("docx")}
              disabled={bulkExporting}
              data-testid="bulk-export-docx-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export DOCX (ZIP)
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by address, quote number, strata..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="quote-search-input"
              />
            </form>
            <Select
              value={statusFilter}
              onValueChange={(v) => updateFilters("status", v)}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {QUOTE_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={companyFilter}
              onValueChange={(v) => updateFilters("company", v)}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="company-filter">
                <SelectValue placeholder="All companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Status Pills */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={statusFilter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => updateFilters("status", "all")}
        >
          All
        </Button>
        {QUOTE_STATUSES.slice(0, 6).map((s) => (
          <Button
            key={s.value}
            variant={statusFilter === s.value ? "default" : "outline"}
            size="sm"
            onClick={() => updateFilters("status", s.value)}
            className={statusFilter === s.value ? "" : getStatusClass(s.value)}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {/* Select All */}
      {quotes.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <button
            type="button"
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="select-all-btn"
          >
            {selectedIds.length === quotes.length && quotes.length > 0 ? (
              <CheckSquare className="h-4 w-4 text-primary" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {selectedIds.length === quotes.length && quotes.length > 0 ? "Deselect all" : "Select all"}
          </button>
        </div>
      )}

      {/* Quotes List */}
      {quotes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No quotes found</p>
            {(isOfficeManager || isQuoter) && (
              <Link to="/quotes/new">
                <Button variant="link" className="mt-2">
                  Create your first quote
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {quotes.map((quote) => {
            const isSelected = selectedIds.includes(quote.id);
            return (
            <div key={quote.id} className="flex items-stretch gap-2">
              <button
                type="button"
                onClick={() => toggleSelect(quote.id)}
                className="flex items-center px-2 hover:bg-muted/50 rounded-md transition-colors"
                data-testid={`select-quote-${quote.id}`}
                aria-label={isSelected ? "Deselect quote" : "Select quote"}
              >
                {isSelected ? (
                  <CheckSquare className="h-5 w-5 text-primary" />
                ) : (
                  <Square className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
              <Link
                to={`/quotes/${quote.id}`}
                className="block flex-1"
                data-testid={`quote-card-${quote.id}`}
              >
                <Card className={`hover:shadow-md transition-shadow ${isSelected ? "ring-2 ring-primary/40" : ""}`}>
                  <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-medium text-primary">
                          {quote.quote_number}
                        </span>
                        <Badge
                          variant="outline"
                          className={getStatusClass(quote.status)}
                        >
                          {quote.status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold">{quote.property_address}</h3>
                      <p className="text-sm text-muted-foreground">
                        {quote.company_name}
                        {quote.property_manager_name &&
                          ` • ${quote.property_manager_name}`}
                      </p>
                      {quote.strata_plan_number && (
                        <p className="text-xs text-muted-foreground font-mono">
                          Strata: {quote.strata_plan_number}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold">
                          {formatCurrency(quote.total_amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(quote.date_quoted)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeleteQuoteId(quote.id);
                        }}
                        data-testid={`delete-quote-${quote.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            </div>
            );
          })}
        </div>
      )}
      
      {/* Delete Confirmation */}
      <Dialog open={!!deleteQuoteId} onOpenChange={() => setDeleteQuoteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quote</DialogTitle>
            <DialogDescription>
              Are you sure? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteQuoteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteQuote}>Delete Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesList;
